import { Navigate, useParams } from "react-router-dom";

/**
 * Legacy URL redirects.
 *
 * Department-based URLs (`/departments/*`, `/workspaces/:ws/departments/*`,
 * `/creative/*`, `/marketing/*`, `/businessops/*`) are deprecated and no
 * longer have a UI surface. Anyone landing on one of these URLs — whether
 * via an old bookmark, marketing CTA, or the browser back button — is
 * forwarded with `replace` to a canonical non-legacy page so they never
 * see the department UI and their history doesn't grow a stale entry.
 */

export { CoAgentSlugRedirect, AgentDetailRedirect } from "@/app/routes/LegacyRedirects";

/** Map a (dept, teamSlug) pair to a canonical non-departments URL. */
function resolveCanonicalPath(dept?: string, teamSlug?: string): string {
  // Team-level routes get mapped to their canonical short URL when one exists.
  if (teamSlug) {
    switch (teamSlug) {
      case "brand-dna":
        return "/brand-dna";
      case "products":
        return "/products";
      case "ads":
      case "video-ads":
        return "/video-ads";
      case "static-ads":
        return "/static-ads";
      case "paid-campaigns":
        return "/paid-campaigns";
      case "google-marketing":
      case "google-ads":
        return "/google-campaigns";
      case "social":
      case "organic":
      case "content":
        return "/organic-campaigns";
      case "videos":
      case "viral-video":
        return "/video-ads";
      default:
        return "/video-ads";
    }
  }
  // Department-level routes — pick a sensible canonical home.
  switch (dept) {
    case "creative":
      return "/video-ads";
    case "marketing":
      return "/organic-campaigns";
    case "businessops":
    case "strategy":
      return "/workspaces";
    default:
      return "/workspaces";
  }
}

/** Old `/departments/*` paths → canonical non-departments URL. */
export function DepartmentLegacyRedirect() {
  const { dept, teamSlug } = useParams();
  return <Navigate to={resolveCanonicalPath(dept, teamSlug)} replace />;
}

/** Workspace-scoped `/workspaces/:ws/departments/*` → canonical non-departments URL. */
export function WorkspaceDepartmentRedirect() {
  const { dept, teamSlug } = useParams();
  return <Navigate to={resolveCanonicalPath(dept, teamSlug)} replace />;
}

/** Short department paths (`/creative`, `/marketing`, `/businessops`) → canonical URL. */
export function ShortDepartmentRedirect({
  dept,
}: {
  dept: "marketing" | "creative" | "businessops";
}) {
  return <Navigate to={resolveCanonicalPath(dept)} replace />;
}

/** Short team paths (`/creative/:teamSlug`) → canonical URL. */
export function ShortTeamRedirect({
  dept,
}: {
  dept: "marketing" | "creative" | "businessops";
}) {
  const { teamSlug } = useParams<{ teamSlug: string }>();
  return <Navigate to={resolveCanonicalPath(dept, teamSlug)} replace />;
}

/** `/settings/agents/sessions/:sessionId` → `/console/sessions/:sessionId` (Phase 3.5). */
export function LegacySessionRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={`/console/sessions/${sessionId ?? ""}`} replace />;
}

/** Old `/workspaces/:id/products` → canonical `/products`. */
export function WorkspaceProductsRedirect() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return <Navigate to={`/products?ws=${workspaceId ?? ""}`} replace />;
}
